import { authenticatedFetch } from "./api";

const REF_COOKIE_NAME = "haven_ref";

/**
 * Read the `haven_ref` referral code (left by the website /invite page on
 * `.starthaven.com`, readable here because `account.starthaven.com` shares the
 * registrable domain) and attribute the install to the sharer.
 *
 * Called fire-and-forget once the user is signed in. The backend endpoint is
 * write-once + validated, so calling it on every sign-in is safe/idempotent.
 * This must NEVER throw — it runs inside the auth flow and must not block it.
 */
export async function captureReferral(): Promise<void> {
  try {
    const match = document.cookie.match(/(?:^|;\s*)haven_ref=([^;]*)/);
    if (!match) return;
    const refCode = decodeURIComponent(match[1]);
    if (!refCode) return;

    const response = await authenticatedFetch("/my-referral", {
      method: "POST",
      body: JSON.stringify({ refCode }),
    });

    // Clear the cookie only on a TERMINAL outcome so a transient failure doesn't
    // permanently drop a valid referral. The endpoint always returns 200 (even
    // for failures), so inspect the body: keep the cookie on a non-OK response
    // (e.g. 401) or a backend error (reason "error") so the next sign-in retries
    // within the 30-day window; clear it on any definitive result (attributed,
    // self, already-set, or invalid code).
    let terminal = false;
    if (response.ok) {
      try {
        const data = (await response.json()) as { reason?: string | null };
        terminal = data?.reason !== "error";
      } catch {
        terminal = true; // 200 with an unparseable body — treat as done.
      }
    }
    if (terminal) {
      document.cookie = `${REF_COOKIE_NAME}=; domain=.starthaven.com; path=/; max-age=0`;
    }
  } catch {
    // Swallow all errors — referral capture must never block auth.
  }
}
